import React, { useState, useMemo } from 'react';
import { Target, RefreshCw, AlertTriangle } from 'lucide-react';

const EVCalculator = ({ odds, fixture }) => {
  const [probabilities, setProbabilities] = useState({
    0: 50, // Home Win
    1: 25, // Draw  
    2: 25  // Away Win
  });
  const [bankroll, setBankroll] = useState(1000);
  const [useKelly, setUseKelly] = useState(true);

  // Extract best odds for each outcome
  const oddsData = useMemo(() => {
    if (!odds?.preMatch?.[0]?.bookmakers) return null;

    const outcomes = ['Home', 'Draw', 'Away'];
    const extractedOdds = outcomes.map(outcome => {
      let bestOdd = 0;
      let bestBookmaker = '';

      odds.preMatch[0].bookmakers.forEach(bookmaker => {
        const matchWinnerBet = bookmaker.bets?.find(bet => bet.name === 'Match Winner');
        const value = matchWinnerBet?.values?.find(v => v.value === outcome);
        if (value && parseFloat(value.odd) > bestOdd) {
          bestOdd = parseFloat(value.odd);
          bestBookmaker = bookmaker.name;
        }
      });

      return {
        name: outcome === 'Home' ? fixture.teams?.home?.name : 
              outcome === 'Away' ? fixture.teams?.away?.name : 'Draw',
        bestOdds: bestOdd,
        bookmaker: bestBookmaker
      };
    });

    return { outcomes: extractedOdds };
  }, [odds, fixture]);

  // Calculate implied probabilities from odds
  const impliedProbabilities = useMemo(() => {
    if (!oddsData) return [];
    return oddsData.outcomes.map(outcome => {
      return outcome.bestOdds > 0 ? (1 / outcome.bestOdds) * 100 : 0;
    });
  }, [oddsData]);

  // Calculate Expected Value for each outcome
  const evCalculations = useMemo(() => {
    if (!oddsData) return [];
    
    return oddsData.outcomes.map((outcome, index) => {
      const userProb = probabilities[index] / 100;
      const impliedProb = impliedProbabilities[index] / 100;
      const odds = outcome.bestOdds;
      
      if (odds <= 0) return null;
      
      const ev = (userProb * odds) - 1;
      const evPercentage = ev * 100;
      
      const b = odds - 1;
      const kelly = ((b * userProb) - (1 - userProb)) / b;
      const kellyPercentage = Math.max(0, kelly * 100);
      
      const recommendedStake = useKelly 
        ? (kellyPercentage / 100) * bankroll
        : ev > 0 ? bankroll * 0.02 : 0;
        
      return {
        ...outcome,
        userProb: userProb * 100,
        impliedProb: impliedProb * 100,
        ev: ev,
        evPercentage: evPercentage,
        kelly: kellyPercentage,
        recommendedStake: Math.round(recommendedStake),
        isPositiveEV: ev > 0,
        probDifference: (userProb * 100) - (impliedProb * 100)
      };
    }).filter(Boolean);
  }, [oddsData, probabilities, impliedProbabilities, bankroll, useKelly]);

  const totalProbability = useMemo(() => {
    return Object.values(probabilities).reduce((sum, prob) => sum + prob, 0);
  }, [probabilities]);

  const isProbabilityValid = useMemo(() => {
    return Math.abs(totalProbability - 100) < 0.1;
  }, [totalProbability]);

  const portfolioEV = useMemo(() => {
    return evCalculations
      .filter(calc => calc.isPositiveEV && calc.recommendedStake > 0)
      .reduce((sum, calc) => sum + (calc.ev * calc.recommendedStake), 0);
  }, [evCalculations]);

  const totalStaked = useMemo(() => {
    return evCalculations
      .filter(calc => calc.isPositiveEV && calc.recommendedStake > 0)
      .reduce((sum, calc) => sum + calc.recommendedStake, 0);
  }, [evCalculations]);

  // Early return after all hooks are defined
  if (!oddsData) {
    return (
      <div className="text-center py-12">
        <Target className="mx-auto mb-4 text-white/40" size={48} />
        <p className="text-white/70 text-lg">No odds data available for EV analysis</p>
      </div>
    );
  }

  const handleProbabilityChange = (index, value) => {
    setProbabilities(prev => ({
      ...prev,
      [index]: parseFloat(value) || 0
    }));
  };

  const normalizeProbabilities = () => {
    const total = Object.values(probabilities).reduce((sum, prob) => sum + prob, 0);
    if (total > 0) {
      const normalized = {};
      Object.keys(probabilities).forEach(key => {
        normalized[key] = Math.round((probabilities[key] / total) * 100 * 10) / 10;
      });
      setProbabilities(normalized);
    }
  };

  return (
    <div className="space-y-6">
      {/* Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-white/[0.05] rounded-xl border border-emerald-500/20">
        <div>
          <label className="block text-sm font-bold text-white/80 mb-2">
            💰 Bankroll ($)
          </label>
          <input
            type="number"
            value={bankroll}
            onChange={(e) => setBankroll(parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-white/[0.03] border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-white"
            placeholder="1000"
          />
        </div>
        
        <div>
          <label className="block text-sm font-bold text-white/80 mb-2">
            📊 Bet Sizing
          </label>
          <select
            value={useKelly ? 'kelly' : 'flat'}
            onChange={(e) => setUseKelly(e.target.value === 'kelly')}
            className="w-full px-3 py-2 bg-white/[0.03] border border-white/10 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-white"
          >
            <option value="kelly">Kelly Criterion</option>
            <option value="flat">2% Flat Stake</option>
          </select>
        </div>

        <div className="flex items-end">
          <button
            onClick={normalizeProbabilities}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white hover:bg-white/20 rounded-lg transition-colors text-sm font-bold"
          >
            <RefreshCw size={16} />
            Normalize to 100%
          </button>
        </div>
      </div>

      {/* Portfolio Summary */}
      {totalStaked > 0 && (
        <div className="bg-gradient-to-r from-emerald-500 to-violet-500 text-white rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-black">${totalStaked}</div>
              <div className="text-white/80 text-sm font-semibold">Total Recommended</div>
            </div>
            <div>
              <div className="text-2xl font-black">${portfolioEV.toFixed(2)}</div>
              <div className="text-white/80 text-sm font-semibold">Expected Profit</div>
            </div>
            <div>
              <div className="text-2xl font-black">{((portfolioEV / totalStaked) * 100).toFixed(1)}%</div>
              <div className="text-white/80 text-sm font-semibold">ROI</div>
            </div>
          </div>
        </div>
      )}

      {/* Probability Warning */}
      {!isProbabilityValid && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle size={18} className="text-amber-300" />
          <span className="text-amber-300 text-sm font-semibold">
            Probabilities sum to {totalProbability.toFixed(1)}% (should be 100%)
          </span>
        </div>
      )}

      {/* Outcomes Analysis */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold text-white mb-3">EV Analysis by Outcome</h3>
        {evCalculations.map((calc, index) => (
          <div key={index} className={`p-4 rounded-xl border ${calc.isPositiveEV ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/[0.03] border-white/10'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-white text-lg">{calc.name}</h4>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${calc.isPositiveEV ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/60'}`}>
                {calc.isPositiveEV ? 'Positive EV' : 'Negative EV'}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div>
                <div className="text-white/60 text-xs">Best Odds</div>
                <div className="text-white font-bold">{calc.bestOdds.toFixed(2)}</div>
                <div className="text-white/40 text-xs">{calc.bookmaker}</div>
              </div>
              <div>
                <div className="text-white/60 text-xs">Your Prob</div>
                <div className="text-white font-bold">{calc.userProb.toFixed(1)}%</div>
                <div className="text-white/40 text-xs">vs {calc.impliedProb.toFixed(1)}% implied</div>
              </div>
              <div>
                <div className="text-white/60 text-xs">Expected Value</div>
                <div className={`font-bold ${calc.ev >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                  {calc.ev >= 0 ? '+' : ''}{calc.evPercentage.toFixed(2)}%
                </div>
              </div>
              <div>
                <div className="text-white/60 text-xs">Kelly %</div>
                <div className="text-white font-bold">{calc.kelly.toFixed(2)}%</div>
                <div className="text-white/40 text-xs">${calc.recommendedStake} stake</div>
              </div>
            </div>

            {/* Probability Input */}
            <div className="flex items-center gap-3">
              <label className="text-white/60 text-sm">Your Probability:</label>
              <input
                type="range"
                min="0"
                max="100"
                step="0.5"
                value={probabilities[index]}
                onChange={(e) => handleProbabilityChange(index, e.target.value)}
                className="flex-1"
              />
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={probabilities[index]}
                onChange={(e) => handleProbabilityChange(index, e.target.value)}
                className="w-20 px-2 py-1 bg-white/[0.03] border border-white/10 rounded text-white text-center"
              />
              <span className="text-white/60">%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Explanation */}
      <div className="p-4 bg-white/[0.03] rounded-xl border border-white/10">
        <h4 className="font-bold text-white mb-2">How it works</h4>
        <ul className="space-y-1 text-white/70 text-sm">
          <li>• Set your probability estimates for each outcome</li>
          <li>• Positive EV = Your probability × Odds > 1</li>
          <li>• Kelly Criterion suggests optimal bet size based on edge</li>
          <li>• Only bet when you have positive expected value</li>
        </ul>
      </div>
    </div>
  );
};

export default EVCalculator;